import actions from 'src/modules/auth/authActions';
import {syncCurrentDivisionIdFromContext} from 'src/modules/auth/authCurrentDivisionStorage';
import AuthCurrentTenant from 'src/modules/auth/authCurrentTenant';
import {getSyntheticTenantAccessContext} from 'src/modules/auth/tenantAccessContextDefaults';

const initialData = {
  currentUser: null as any,
  currentTenant: null,
  currentDivisionId: null as string | null,
  /** Se incrementa al cambiar división (header API); útil como dependencia en efectos. */
  divisionScopeRevision: 0,
  tenantAccessContext: null,
  tenantAccessContextLoadStatus: null as 'loaded' | 'failed' | null,
  loadingInit: true,
  loadingEmailConfirmation: false,
  loadingPasswordResetEmail: false,
  loadingPasswordChange: false,
  loadingVerifyEmail: false,
  loadingPasswordReset: false,
  loadingUpdateProfile: false,
  loadingGoogleCalendar: false,
  loading: false,
  errorMessage: null,
  errorMessageVerifyEmail: null,
  errorMessageGoogleCalendar: null,
};

export default (state = initialData, {type, payload}) => {
  if (type === actions.ERROR_MESSAGE_CLEARED) {
    return {
      ...state,
      errorMessage: null,
    };
  }

  if (type === actions.MERGE_TENANT_USER_DATA) {
    const tenantId = AuthCurrentTenant.get();
    const cu = state.currentUser;
    if (!cu?.tenants || !tenantId || !payload) {
      return state;
    }
    const nextTenants = cu.tenants.map((tu: any) => {
      const tid = tu.tenant?.id ?? tu.tenant;
      if (String(tid) !== String(tenantId)) {
        return tu;
      }
      return {
        ...tu,
        data: {...(tu.data || {}), ...payload},
      };
    });
    return {
      ...state,
      currentUser: {...cu, tenants: nextTenants},
    };
  }

  if (type === actions.CURRENT_USER_REFRESH_SUCCESS) {
    const nextCtx =
      payload.currentUser == null
        ? null
        : payload.tenantAccessContext !== undefined
          ? payload.tenantAccessContext
          : state.tenantAccessContext;
    const currentDivisionId = payload.currentUser == null ? null : syncCurrentDivisionIdFromContext(nextCtx);
    return {
      ...state,
      currentUser: payload.currentUser || null,
      currentTenant: AuthCurrentTenant.selectAndSaveOnStorageFor(payload.currentUser),
      tenantAccessContext: nextCtx,
      tenantAccessContextLoadStatus:
        payload.currentUser == null
          ? null
          : payload.tenantAccessContextLoadStatus !== undefined
            ? payload.tenantAccessContextLoadStatus
            : state.tenantAccessContextLoadStatus,
      currentDivisionId,
    };
  }

  if (type === actions.CURRENT_USER_REFRESH_ERROR) {
    return {
      ...state,
      currentUser: null,
      currentTenant: AuthCurrentTenant.selectAndSaveOnStorageFor(null),
      tenantAccessContext: null,
      tenantAccessContextLoadStatus: null,
      currentDivisionId: null,
    };
  }

  if (type === actions.AUTH_START) {
    return {
      ...state,
      errorMessage: null,
      loading: true,
    };
  }

  if (type === actions.AUTH_SUCCESS) {
    const tenantAccessContext =
      payload.currentUser == null
        ? null
        : payload.tenantAccessContext !== undefined
          ? payload.tenantAccessContext
          : state.tenantAccessContext;
    const tenantAccessContextLoadStatus =
      payload.currentUser == null
        ? null
        : payload.tenantAccessContextLoadStatus !== undefined
          ? payload.tenantAccessContextLoadStatus
          : state.tenantAccessContextLoadStatus;
    const currentDivisionId = payload.currentUser == null ? null : syncCurrentDivisionIdFromContext(tenantAccessContext);

    return {
      ...state,
      currentUser: payload.currentUser || null,
      currentTenant: AuthCurrentTenant.selectAndSaveOnStorageFor(payload.currentUser),
      errorMessage: null,
      loading: false,
      tenantAccessContext,
      tenantAccessContextLoadStatus,
      currentDivisionId,
    };
  }

  if (type === actions.AUTH_ERROR) {
    return {
      ...state,
      currentUser: null,
      currentTenant: null,
      tenantAccessContext: null,
      tenantAccessContextLoadStatus: null,
      currentDivisionId: null,
      errorMessage: payload || null,
      loading: false,
    };
  }

  if (type === actions.EMAIL_CONFIRMATION_START) {
    return {
      ...state,
      loadingEmailConfirmation: true,
    };
  }

  if (type === actions.EMAIL_CONFIRMATION_SUCCESS) {
    return {
      ...state,
      loadingEmailConfirmation: false,
    };
  }

  if (type === actions.EMAIL_CONFIRMATION_ERROR) {
    return {
      ...state,
      loadingEmailConfirmation: false,
    };
  }

  if (type === actions.PASSWORD_RESET_EMAIL_START) {
    return {
      ...state,
      loadingPasswordResetEmail: true,
    };
  }

  if (type === actions.PASSWORD_RESET_EMAIL_SUCCESS) {
    return {
      ...state,
      loadingPasswordResetEmail: false,
    };
  }

  if (type === actions.PASSWORD_RESET_EMAIL_ERROR) {
    return {
      ...state,
      loadingPasswordResetEmail: false,
    };
  }

  if (type === actions.UPDATE_PROFILE_START) {
    return {
      ...state,
      loadingUpdateProfile: true,
    };
  }

  if (type === actions.UPDATE_PROFILE_SUCCESS) {
    return {
      ...state,
      loadingUpdateProfile: false,
    };
  }

  if (type === actions.UPDATE_PROFILE_ERROR) {
    return {
      ...state,
      loadingUpdateProfile: false,
    };
  }

  if (type === actions.PASSWORD_CHANGE_START) {
    return {
      ...state,
      loadingPasswordChange: true,
    };
  }

  if (type === actions.PASSWORD_CHANGE_SUCCESS) {
    return {
      ...state,
      loadingPasswordChange: false,
    };
  }

  if (type === actions.PASSWORD_CHANGE_ERROR) {
    return {
      ...state,
      loadingPasswordChange: false,
    };
  }

  if (type === actions.AUTH_INIT_SUCCESS) {
    const nextCtx =
      payload.currentUser == null ? null : payload.tenantAccessContext !== undefined ? payload.tenantAccessContext : null;
    const currentDivisionId = payload.currentUser == null ? null : syncCurrentDivisionIdFromContext(nextCtx);
    return {
      ...state,
      currentUser: payload.currentUser || null,
      currentTenant: AuthCurrentTenant.selectAndSaveOnStorageFor(payload.currentUser),
      tenantAccessContext: nextCtx,
      tenantAccessContextLoadStatus:
        payload.currentUser == null
          ? null
          : payload.tenantAccessContextLoadStatus !== undefined
            ? payload.tenantAccessContextLoadStatus
            : null,
      currentDivisionId,
      loadingInit: false,
    };
  }

  if (type === actions.AUTH_INIT_ERROR) {
    return {
      ...state,
      currentUser: null,
      currentTenant: null,
      tenantAccessContext: null,
      tenantAccessContextLoadStatus: null,
      currentDivisionId: null,
      loadingInit: false,
    };
  }

  if (type === actions.TENANT_ACCESS_CONTEXT_SUCCESS) {
    const ctx = payload?.context ?? payload;
    const loadStatus = payload?.loadStatus ?? 'loaded';
    const currentDivisionId = syncCurrentDivisionIdFromContext(ctx);
    return {
      ...state,
      tenantAccessContext: ctx || null,
      tenantAccessContextLoadStatus: loadStatus,
      currentDivisionId,
      // Nueva referencia para que createSelector que depende de currentUser invalide caché
      // (customPermissionIds vive en tenantAccessContext; PermissionChecker lo lee del store).
      currentUser: state.currentUser ? {...state.currentUser} : null,
    };
  }

  if (type === actions.TENANT_ACCESS_CONTEXT_FAILED) {
    const syn = getSyntheticTenantAccessContext();
    const currentDivisionId = syncCurrentDivisionIdFromContext(syn);
    return {
      ...state,
      tenantAccessContext: syn,
      tenantAccessContextLoadStatus: 'failed',
      currentDivisionId,
      currentUser: state.currentUser ? {...state.currentUser} : null,
    };
  }

  if (type === actions.TENANT_ACCESS_CONTEXT_CLEARED) {
    return {
      ...state,
      tenantAccessContext: null,
      tenantAccessContextLoadStatus: null,
      currentDivisionId: null,
      currentUser: state.currentUser ? {...state.currentUser} : null,
    };
  }

  if (type === actions.CURRENT_DIVISION_SET) {
    const next = payload?.currentDivisionId ?? null;
    const prev = state.currentDivisionId ?? null;
    const divisionScopeRevision =
      next === prev ? state.divisionScopeRevision : (state.divisionScopeRevision ?? 0) + 1;
    return {
      ...state,
      currentDivisionId: next,
      divisionScopeRevision,
    };
  }

  if (type === actions.EMAIL_VERIFY_START) {
    return {
      ...state,
      loadingVerifyEmail: true,
      errorMessageVerifyEmail: null,
    };
  }

  if (type === actions.EMAIL_VERIFY_SUCCESS) {
    return {
      ...state,
      loadingVerifyEmail: false,
      errorMessageVerifyEmail: null,
    };
  }

  if (type === actions.EMAIL_VERIFY_ERROR) {
    return {
      ...state,
      loadingVerifyEmail: false,
      errorMessageVerifyEmail: payload,
    };
  }

  // Google Calendar reducers
  if (type === actions.GOOGLE_CALENDAR_CONNECT_START) {
    return {
      ...state,
      loadingGoogleCalendar: true,
      errorMessageGoogleCalendar: null,
    };
  }

  if (type === actions.GOOGLE_CALENDAR_CONNECT_SUCCESS) {
    return {
      ...state,
      loadingGoogleCalendar: false,
      errorMessageGoogleCalendar: null,
    };
  }

  if (type === actions.GOOGLE_CALENDAR_CONNECT_ERROR) {
    return {
      ...state,
      loadingGoogleCalendar: false,
      errorMessageGoogleCalendar: payload,
    };
  }

  // Google Calendar disconnect reducers
  if (type === actions.GOOGLE_CALENDAR_DISCONNECT_START) {
    return {
      ...state,
      loadingGoogleCalendar: true,
      errorMessageGoogleCalendar: null,
    };
  }

  if (type === actions.GOOGLE_CALENDAR_DISCONNECT_SUCCESS) {
    return {
      ...state,
      loadingGoogleCalendar: false,
      errorMessageGoogleCalendar: null,
    };
  }

  if (type === actions.GOOGLE_CALENDAR_DISCONNECT_ERROR) {
    return {
      ...state,
      loadingGoogleCalendar: false,
      errorMessageGoogleCalendar: payload,
    };
  }

  return state;
};
